import {EntityArray} from '../../core/entityArray';
import {LookinChatSDK} from '../../lib/lookinChatSDK';
import {State, StateType} from '../state';
import {User} from '../user';

/**
 * Message State
 *
 * @category Message Entity
 * @extends State
 * */
export class MessageState extends State {

  /**
   * Message State type
   * */
  type: MessageStateType = 'create'

  /**
   * Message State content, used for reactions
   * */
  content: string = null;

  /**
   * Only for internal user, no direct calls
   *
   * @ignore
   * @internal
   * */
  constructor(type: MessageStateType = 'create', content: string = null) {
    super();

    this.type = type;
    this.content = content;
  }
}

/**
 * Message collection
 *
 * @category Message Entity
 * */
export class MessageStateCollection extends EntityArray<MessageState> {

  /**
   * @return first state with `create` {@link MessageStateType}
   * */
  getCreateState() {
    return this.getByField('type', 'create');
  }

  /**
   * @return all states with `read` {@link MessageStateType}
   * */
  getReadStates() {
    return this.filter(item => item.type === 'read');
  }

  /**
   * @return all states with `reaction` {@link MessageStateType}
   * */
  getReactionStates() {
    return this.filter(item => item.type === 'reaction');
  }

  /**
   * @return all reactions as content
   * */
  getReactionStatesOnlyContent(): string[] {
    return this.filter(item => item.type === 'reaction').map(item => item.content);
  }

  /**
   * @param reaction string
   *
   * @return if current user reacted on this message
   * */
  hasOwnReaction(reaction: string) {
    return this.getByUserAndType('reaction', LookinChatSDK.user)?.content === reaction;
  }

  /**
   * @return grouped reaction by type
   *```json
   * {
   *   '😭': [
   *     { stateObject },
   *     { stateObject },
   *   ],
   *   '😵': [
   *     { stateObject }
   *   ]
   * }
   * ```
   * */
  getReactionGrouped() {
    const reactionStates = this.getReactionStates();

    if (reactionStates?.length) {
      const groupByCategory = reactionStates.reduce((group: any, state) => {
        const {content} = state;
        group[content] = group[content] ?? [];
        group[content].push(state);
        return group;
      }, {});

      return groupByCategory;
    }

    return null
  }

  /**
   * @param reaction string
   *
   * @return count of reactions
   * */
  getReactionCount(reaction: string) {
    return this.getReactionStatesByReaction(reaction)?.length
  }

  /**
   * @param reaction string
   *
   * @return all states with this reaction
   * */
  getReactionStatesByReaction(reaction: string) {
    return this.filter((item) => item.type === 'reaction' && item.content === reaction)
  }

  /**
   * @param type of message state
   * @param user by who make search
   *
   * @return first state of this user with this state
   *
   * @internal
   * */
  getByUserAndType(type: MessageStateType, user: User) {
    return this.find((item) => item.type === type && item.user.id.toString() === user.id.toString());
  }

  /**
   * @param type of message state
   * @param user by who make search
   *
   * @return remove state of this type by this user from array
   *
   * @internal
   * */
  removeByUserAndType(type: MessageStateType, user: User) {
    const index = this.findIndex((item) => item.type === type && item.user.id.toString() === user.id.toString());
    if (index > -1) {
      this.splice(index, 1);
    }
  }

  /**
   * @return if message was changed
   * */
  isChanged() {
    return this.getByField('type', 'edit');
  }
}

export type MessageStateType = 'read' |
  'reaction' |
  'deleteReaction' |
  'reactionRead' |
  'mentionRead' |
  StateType;
